import { createContext, useContext, useState, useCallback } from "react";

const HotspotEditorContext = createContext();

export function useHotspotEditor() {
   return useContext(HotspotEditorContext);
}

export function HotspotEditorProvider({ children }) {
   // Editor mode: 'select' | 'add' | 'edit-position' | 'edit-normal'
   const [editorMode, setEditorMode] = useState('select');
   
   // Currently selected hotspot index
   const [selectedHotspotIndex, setSelectedHotspotIndex] = useState(null);
   
   // Hotspots array (editable copy)
   const [hotspots, setHotspots] = useState([]);
   
   // Track if hotspots have been modified
   const [isDirty, setIsDirty] = useState(false);

   // Initialize hotspots from variant
   const initializeHotspots = useCallback((variantHotspots) => {
      if (variantHotspots && Array.isArray(variantHotspots)) {
         // Deep clone to avoid mutating original
         setHotspots(JSON.parse(JSON.stringify(variantHotspots)));
      } else {
         setHotspots([]);
      }
      setIsDirty(false);
      setSelectedHotspotIndex(null);
   }, []);

   // Add a new hotspot
   const addHotspot = useCallback((position, normal) => {
      const newHotspot = {
         id: hotspots.length > 0 ? Math.max(...hotspots.map(h => h.id)) + 1 : 0,
         visibility: "dynamic",
         labelAlignment: "right",
         contentType: "text",
         title: `Hotspot ${hotspots.length + 1}`,
         content: "Enter description here",
         position: {
            x: `${position.x}m`,
            y: `${position.y}m`,
            z: `${position.z}m`
         },
         normal: {
            x: `${normal.x}m`,
            y: `${normal.y}m`,
            z: `${normal.z}m`
         }
      };
      
      setHotspots(prev => [...prev, newHotspot]);
      setSelectedHotspotIndex(hotspots.length);
      setIsDirty(true);
      setEditorMode('select');
      
      return newHotspot;
   }, [hotspots]);

   // Update a hotspot property
   const updateHotspot = useCallback((index, updates) => {
      setHotspots(prev => {
         const newHotspots = [...prev];
         newHotspots[index] = { ...newHotspots[index], ...updates };
         return newHotspots;
      });
      setIsDirty(true);
   }, []);

   // Update hotspot position
   const updateHotspotPosition = useCallback((index, position) => {
      updateHotspot(index, {
         position: {
            x: `${position.x}m`,
            y: `${position.y}m`,
            z: `${position.z}m`
         }
      });
   }, [updateHotspot]);

   // Update hotspot normal
   const updateHotspotNormal = useCallback((index, normal) => {
      updateHotspot(index, {
         normal: {
            x: `${normal.x}m`,
            y: `${normal.y}m`,
            z: `${normal.z}m`
         }
      });
   }, [updateHotspot]);

   // Delete a hotspot
   const deleteHotspot = useCallback((index) => {
      setHotspots(prev => prev.filter((_, i) => i !== index));
      setSelectedHotspotIndex(null);
      setIsDirty(true);
   }, []);

   // Duplicate a hotspot
   const duplicateHotspot = useCallback((index) => {
      const hotspot = hotspots[index];
      if (!hotspot) return;
      
      const newHotspot = {
         ...JSON.parse(JSON.stringify(hotspot)),
         id: Math.max(...hotspots.map(h => h.id)) + 1,
         title: `${hotspot.title} (copy)`
      };
      
      setHotspots(prev => [...prev, newHotspot]);
      setSelectedHotspotIndex(hotspots.length);
      setIsDirty(true);
   }, [hotspots]);

   // Export hotspots as JSON
   const exportHotspots = useCallback(() => {
      return JSON.stringify(hotspots, null, 2);
   }, [hotspots]);

   // Get selected hotspot
   const selectedHotspot = selectedHotspotIndex !== null ? hotspots[selectedHotspotIndex] : null;

   const value = {
      editorMode,
      setEditorMode,
      selectedHotspotIndex,
      setSelectedHotspotIndex,
      selectedHotspot,
      hotspots,
      setHotspots,
      isDirty,
      initializeHotspots,
      addHotspot,
      updateHotspot,
      updateHotspotPosition,
      updateHotspotNormal,
      deleteHotspot,
      duplicateHotspot,
      exportHotspots
   };

   return (
      <HotspotEditorContext.Provider value={value}>
         {children}
      </HotspotEditorContext.Provider>
   );
}


